import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import Auth0ProviderWithHistory from "auth0ProviderWithHistory";
import Auth0Wrapper from "auth0Wrapper";
import React from "react";
import { createRoot } from "react-dom/client";
import { ErrorBoundary } from "react-error-boundary";
import { Provider } from "react-redux";
import App from "./App";
import ErrorFallback from "./ErrorFallback";
import "./index.css";
import Store from "./redux/store";
import { isChunkError, reloadOnChunkError } from "./util/lazyWithRetry";
import { apiUrl, socketUrl } from "util/constants";

// Warm the DNS+TCP+TLS connection to the cross-origin hosts the app talks to
// immediately after bootstrap (API, realtime socket, Auth0). On high-latency or
// proxied corporate networks each origin otherwise costs a full round-trip
// before its first request can even start, which is a large share of the
// initial-load delay those users experience.
const preconnectOrigins = () => {
  const auth0Domain = process.env.REACT_APP_AUTH0_DOMAIN;
  const candidates = [
    apiUrl,
    socketUrl,
    auth0Domain ? `https://${auth0Domain}` : "",
  ];

  const seen = new Set<string>();

  for (const value of candidates) {
    if (!value) continue;

    let origin: string;
    try {
      origin = new URL(value).origin;
    } catch {
      continue;
    }

    if (seen.has(origin)) continue;
    seen.add(origin);

    const preconnect = document.createElement("link");
    preconnect.rel = "preconnect";
    preconnect.href = origin;
    preconnect.crossOrigin = "anonymous";
    document.head.appendChild(preconnect);

    const dnsPrefetch = document.createElement("link");
    dnsPrefetch.rel = "dns-prefetch";
    dnsPrefetch.href = origin;
    document.head.appendChild(dnsPrefetch);
  }
};

preconnectOrigins();

if (process.env.REACT_APP_ENVIRONMENT === "production") {
  const initLogRocket = async () => {
    const { default: LogRocket } = await import("logrocket");
    LogRocket.init("p8awhu/fuzey-production", {
      release: process.env.REACT_APP_SENTRY_RELEASE,
    });
  };

  if (typeof requestIdleCallback === "function") {
    requestIdleCallback(() => {
      initLogRocket();
    });
  } else {
    setTimeout(() => {
      initLogRocket();
    }, 0);
  }
}

const container = document.getElementById("root");
const root = createRoot(container!);

// Allow disabling React.StrictMode at runtime for e2e tests or debugging.
// Can be toggled via window.__strict_mode_disabled__ = true (e.g. page.addInitScript)
// or by setting localStorage key DISABLE_STRICT_MODE = "true" before app bootstraps.
const strictModeDisabled = Boolean(
  (window as any).__strict_mode_disabled__ ||
    localStorage.getItem("DISABLE_STRICT_MODE") === "true"
);

window.addEventListener("unhandledrejection", (event) => {
  const { reason } = event;

  if (isChunkError(reason)) {
    // eslint-disable-next-line
    console.warn("Detected failed chunk load, reloading page... ", reason);
    reloadOnChunkError();
  }
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: unknown) => {
        const err = error as any;
        const status: number | undefined =
          err?.status ?? err?.response?.status ?? err?.cause?.status;

        // Never retry 4xx errors.
        if (typeof status === "number" && status >= 400 && status < 500) {
          return false;
        }

        // Retry only once for network errors (no status) and 5xx.
        const isNetworkError = status == null;
        const is5xx = typeof status === "number" && status >= 500;
        if (isNetworkError || is5xx) {
          return failureCount < 1;
        }

        // Do not retry other 4xx, timeouts, validation, etc.
        return false;
      },
      retryDelay: (attemptIndex) => {
        // attemptIndex starts at 0; we only allow 1 retry anyway.
        return 500 * (attemptIndex + 1);
      },
    },
  },
});

if (strictModeDisabled) {
  root.render(
    <Provider store={Store}>
      <Auth0ProviderWithHistory>
        <Auth0Wrapper>
          <QueryClientProvider client={queryClient}>
            <ErrorBoundary
              FallbackComponent={ErrorFallback}
              onError={(error) => {
                // eslint-disable-next-line
                console.error(
                  "Global App Failure",
                  error,
                  window.location.href,
                  localStorage.getItem("fuzey:merchant:id") || "undefined"
                );
              }}
            >
              <App />
            </ErrorBoundary>
          </QueryClientProvider>
        </Auth0Wrapper>
      </Auth0ProviderWithHistory>
    </Provider>
  );
} else {
  root.render(
    <React.StrictMode>
      <Provider store={Store}>
        <Auth0ProviderWithHistory>
          <Auth0Wrapper>
            <QueryClientProvider client={queryClient}>
              <ErrorBoundary
                FallbackComponent={ErrorFallback}
                onError={(error) => {
                  // eslint-disable-next-line
                  console.error(
                    "Global App Failure",
                    error,
                    window.location.href,
                    localStorage.getItem("fuzey:merchant:id") || "undefined"
                  );
                }}
              >
                <App />
              </ErrorBoundary>
            </QueryClientProvider>
          </Auth0Wrapper>
        </Auth0ProviderWithHistory>
      </Provider>
    </React.StrictMode>
  );
}
